"use client";

import * as React from "react";
import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  cn,
  AreaChartProps,
} from "visuais";

export function AreaChart({
  data,
  config,
  className,
  height = 250,
  width = "100%",
  xKey,
  yKeys,
  variant = "default",
  fillOpacity = 0.4,
  strokeWidth = 2,
  smooth = true,
  stacked = false,
  showTooltip = true,
  showGrid = true,
  showLegend = false,
  showGradient = false,
  showXAxis = true,
  showYAxis = true,
  loading = false,
  error = null,
  xAxisFormatter,
  yAxisFormatter,
}: AreaChartProps) {
  const chartType = smooth ? "monotone" : "linear";
  const stackId = stacked ? "stack" : undefined;

  // Determinar se deve usar gradiente baseado na variante
  const useGradient = showGradient || variant === "gradient";

  if (loading) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-muted-foreground text-sm">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-destructive text-sm text-center">
          Erro: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <ChartContainer
        config={config}
        className="w-full"
        style={{ height, width }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RechartsAreaChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
            )}

            {showXAxis && (
              <XAxis
                dataKey={xKey}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={xAxisFormatter}
              />
            )}

            {showYAxis && (
              <YAxis
                yAxisId="left"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                domain={[0, "auto"]}
                tickFormatter={yAxisFormatter}
              />
            )}

            {showTooltip && (
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            )}

            {useGradient && (
              <defs>
                {yKeys.map((key, index) => {
                  const seriesConfig = config[key];
                  const color =
                    seriesConfig?.color ||
                    `hsl(var(--chart-${(index % 5) + 1}))`;

                  return (
                    <linearGradient
                      key={`gradient-${key}`}
                      id={`fill-${key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  );
                })}
              </defs>
            )}

            {yKeys.map((key, index) => {
              const seriesConfig = config[key];
              if (!seriesConfig) return null;

              const color =
                seriesConfig.color || `hsl(var(--chart-${(index % 5) + 1}))`;
              const fill = useGradient ? `url(#fill-${key})` : color;

              return (
                <Area
                  key={key}
                  type={variant === "step" ? "step" : chartType}
                  dataKey={key}
                  stackId={stackId}
                  stroke={color}
                  fill={fill}
                  fillOpacity={useGradient ? 1 : fillOpacity}
                  strokeWidth={strokeWidth}
                  yAxisId="left"
                />
              );
            })}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </ChartContainer>

      {showLegend && (
        <div className="mt-4">
          <ChartLegend content={<ChartLegendContent />} />
        </div>
      )}
    </div>
  );
}
